import React from 'react';
import { Button } from './ui/button';
import { ArrowRight } from 'lucide-react';
import CallFrontDeskLine from './CallFrontDeskLine';

/**
 * Reusable bottom-of-page CTA banner. Used by the new audience landing pages
 * AND the homepage gateway so every public route ends on a Discovery Call CTA
 * without each page reimplementing the same block.
 *
 * Phase 22 (information-architecture restructure).
 */
export const FinalCTABanner = ({
  eyebrow = 'Next Step',
  headline = 'Train your mind. Elevate your game.',
  subhead = 'A 30-minute Discovery Call is the no-pressure way to map your plan. Bring your scenario, your sport, your season — we’ll walk it through together.',
  primaryHref,
  primaryLabel = 'Book a Discovery Call',
  secondaryHref = '/quiz',
  secondaryLabel = 'Take the Free Mindset Quiz',
  testId = 'final-cta-banner',
}) => {
  return (
    <section
      className="relative py-16 sm:py-20 bg-gradient-to-br from-[#0a0d0e] via-[#0f1416] to-[#0a0d0e] text-white overflow-hidden"
      data-testid={testId}
    >
      {/* Same diagonal-line texture used in AudienceHero */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-[0.06]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(135deg, rgba(255,255,255,0.5) 0 1px, transparent 1px 22px)',
        }}
      />
      <div className="relative max-w-[860px] mx-auto px-5 sm:px-6 text-center">
        <div className="font-mono-accent uppercase tracking-[0.25em] text-[10px] text-[#4FBCC7] font-bold mb-4">
          {eyebrow}
        </div>
        <h2 className="font-heading text-[2rem] sm:text-[2.5rem] font-bold tracking-tight leading-[1.1] mb-4">
          {headline}
        </h2>
        <p className="text-[16px] sm:text-[17px] text-white/65 leading-[1.55] max-w-[55ch] mx-auto mb-8">
          {subhead}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <Button
            asChild
            className="bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-12 px-6 text-[14.5px] rounded-md active:scale-[0.98] shadow-md"
          >
            <a
              href={primaryHref}
              data-testid={`${testId}-primary-cta`}
              target={primaryHref && primaryHref.startsWith('http') ? '_blank' : undefined}
              rel={primaryHref && primaryHref.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {primaryLabel}
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
          <Button
            asChild
            variant="outline"
            className="h-12 px-6 text-[14.5px] rounded-md font-semibold border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
          >
            <a href={secondaryHref} data-testid={`${testId}-secondary-cta`}>
              {secondaryLabel}
            </a>
          </Button>
        </div>
        {/* Phase 35 — front-desk alternative path. Sits BELOW the CTA buttons
            so it never out-shouts the booking primary; still gives prospects
            a high-touch option if they prefer to talk to a human first. */}
        <div className="mt-5">
          <CallFrontDeskLine tone="dark" testId={`${testId}-call-line`} />
        </div>
      </div>
    </section>
  );
};

export default FinalCTABanner;
